// filepath: apps/web/src/features/admin/tier/TierPanelPage.tsx
//
// FR-UI-ADM-14 — top-level page composition for the Tier & Quota
// admin panel.
//
// Composes:
//   - useTenant (resolves tenantSlug for the cache key)
//   - useTierQuota (TanStack Query hook — emits the 5
//     FR-UI-CORE-10 fetch states)
//   - the 5 fetch states (loading / success / fetch-failure /
//     permission-denied / rate-limit)
//
// Read-only: there is no save / submit / mutation path. The
// panel surfaces a live snapshot of the tenant's tier limits and
// usage; updating tier or quota belongs to a separate surface
// (out of scope for FR-UI-ADM-14).
//
// INV-9: no dangerouslySetInnerHTML. All server strings render as
// React text nodes via JSX `{value}` interpolation.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { Locale } from "@/i18n/useTranslation";
import { useTenant } from "@/tenant/TenantProvider";
import { useTierQuota } from "./queries";
import type { TierFetchError, TierQuotaResponse, TierUsage } from "./types";
import { CONCURRENT_SANDBOXES_UNAVAILABLE } from "./types";

interface TierPanelPageProps {
  /** Active locale — forwarded for i18n-aware labels. */
  locale: Locale;
}

/**
 * TierPanelPage — admin viewer page. Composes the hook + 5 fetch
 * states + a success summary block. No save / mutation path —
 * the panel is read-only by contract.
 */
export function TierPanelPage({
  locale: _locale,
}: TierPanelPageProps): React.ReactElement {
  const { t } = useTranslation();
  const { tenantSlug } = useTenant();
  const query = useTierQuota(tenantSlug);

  // ── loading ──────────────────────────────────────────────────────────
  if (query.state === "loading") {
    return (
      <div
        className="space-y-4 animate-pulse p-6"
        data-testid="tier-loading-skeleton"
        aria-busy="true"
        aria-live="polite"
      >
        <div className="h-10 bg-muted rounded" />
        <div className="h-24 bg-muted rounded" />
        <div className="h-32 bg-muted rounded" />
      </div>
    );
  }

  // ── error states ─────────────────────────────────────────────────────
  if (query.error) {
    return (
      <ErrorState
        error={query.error}
        onRetry={() => {
          // No-op refetch: TanStack Query exposes a refetch
          // helper on the underlying UseQueryResult; the typed
          // envelope here intentionally hides it. The retry
          // button triggers a window-level reload, which forces
          // a fresh cache miss and re-issues the fetch.
          if (typeof window !== "undefined") {
            window.location.reload();
          }
        }}
      />
    );
  }

  // ── success ──────────────────────────────────────────────────────────
  if (!query.data) {
    return (
      <p className="text-muted-foreground text-sm p-6" data-testid="tier-empty">
        {t("admin.tier.empty", "No tier data available.")}
      </p>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="tier-panel-page">
      <header>
        <h1 className="text-lg font-semibold" data-testid="tier-page-title">
          {t("admin.tier.title", "Tier & Quota")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("admin.tier.subtitle", "Subscription tier and quota usage")}
        </p>
      </header>
      <TierSummary data={query.data} />
      {/* TierPanelTable will replace this placeholder when authored. */}
      <div
        className="rounded border border-dashed border-border p-4 text-sm text-muted-foreground"
        data-testid="tier-table-placeholder"
      >
        {t(
          "admin.tier.table_placeholder",
          "Detailed limits table coming soon.",
        )}
      </div>
    </div>
  );
}

// --- TierSummary ---------------------------------------------------------

interface TierSummaryProps {
  data: TierQuotaResponse;
}

/**
 * TierSummary — compact summary of tier + 3 usage fields. The
 * per-limit grid belongs to TierPanelTable (future).
 */
function TierSummary({ data }: TierSummaryProps): React.ReactElement {
  const { t } = useTranslation();
  const usage: TierUsage = data.usage;

  const storageMb = Math.round(usage.storage_bytes / (1024 * 1024));
  const fileStorageMb = Math.round(usage.file_storage_bytes / (1024 * 1024));
  const concurrent =
    usage.concurrent_sandboxes === CONCURRENT_SANDBOXES_UNAVAILABLE
      ? t("admin.tier.unavailable", "Unavailable")
      : String(usage.concurrent_sandboxes);

  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
      data-testid="tier-summary"
      data-tier={data.tier}
    >
      <SummaryCard
        label={t("admin.tier.summary.tier", "Tier")}
        value={data.tier}
      />
      <SummaryCard
        label={t("admin.tier.summary.storage", "Storage (MB)")}
        value={String(storageMb)}
      />
      <SummaryCard
        label={t("admin.tier.summary.file_storage", "File storage (MB)")}
        value={String(fileStorageMb)}
      />
      <SummaryCard
        label={t(
          "admin.tier.summary.concurrent_sandboxes",
          "Concurrent sandboxes",
        )}
        value={concurrent}
      />
    </div>
  );
}

interface SummaryCardProps {
  label: string;
  value: string;
}

function SummaryCard({ label, value }: SummaryCardProps): React.ReactElement {
  return (
    <div
      className="rounded border border-border p-4"
      data-testid="tier-summary-card"
    >
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-lg font-medium">{value}</div>
    </div>
  );
}

// --- ErrorState component ------------------------------------------------

interface ErrorStateProps {
  error: TierFetchError;
  onRetry: () => void;
}

function ErrorState({ error, onRetry }: ErrorStateProps): React.ReactElement {
  const { t } = useTranslation();

  const title = ((): string => {
    switch (error.kind) {
      case "permission-denied":
        return t(
          "admin.errors.permission_denied",
          "You do not have permission to view this resource.",
        );
      case "rate-limit":
        return t("admin.errors.rate_limit", "Too many requests. Please wait.");
      case "fetch-failure":
      default:
        return t("admin.errors.fetch_failure", "Failed to load tier data.");
    }
  })();

  return (
    <div
      className="space-y-4 p-6"
      data-testid="tier-error-state"
      data-error-kind={error.kind}
    >
      <p className="text-sm text-destructive">{title}</p>
      <p className="text-xs text-muted-foreground">{error.message}</p>
      {error.kind === "rate-limit" &&
        typeof error.retryAfterSeconds === "number" && (
          <p className="text-xs text-muted-foreground">
            {t("admin.tier.retry_after", "Retry available in {{seconds}}s.", {
              seconds: error.retryAfterSeconds,
            })}
          </p>
        )}
      {error.retriable && (
        <button
          type="button"
          className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          data-testid="tier-retry-button"
          onClick={onRetry}
        >
          {t("common.retry", "Retry")}
        </button>
      )}
    </div>
  );
}
